import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Gauge, Activity, Clock, Target, AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface OEEDashboardProps {
  dateRange: {
    start: Date;
    end: Date;
  };
}

export function OEEDashboard({ dateRange }: OEEDashboardProps) {
  const { data: machines } = useQuery({
    queryKey: ["machines-oee"],
    queryFn: async () => {
      const { data, error } = await api.from("machines").select("*").eq("is_active", true);
      if (error) throw error;
      return data;
    },
  });

  const { data: processRecords } = useQuery({
    queryKey: ["process-records-oee", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("process_records")
        .select("*")
        .gte("created_at", dateRange.start.toISOString())
        .lte("created_at", dateRange.end.toISOString());
      if (error) throw error;
      return data;
    },
  });

  const { data: pauses } = useQuery({
    queryKey: ["process-pauses-oee", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("process_pauses")
        .select("*")
        .gte("created_at", dateRange.start.toISOString())
        .lte("created_at", dateRange.end.toISOString());
      if (error) throw error;
      return data;
    },
  });

  const { data: qualityInspections } = useQuery({
    queryKey: ["quality-oee", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select("*")
        .gte("inspection_date", dateRange.start.toISOString())
        .lte("inspection_date", dateRange.end.toISOString());
      if (error) throw error;
      return data;
    },
  });

  // Calculate OEE Components
  // Availability = (Operating Time / Planned Production Time) * 100
  // Performance = (Ideal Cycle Time × Total Count) / Operating Time * 100
  // Quality = (Good Count / Total Count) * 100
  // OEE = Availability × Performance × Quality

  const totalPlannedTime = processRecords?.length
    ? processRecords.reduce((sum, p) => {
        if (p.start_time && p.end_time) {
          return sum + (new Date(p.end_time).getTime() - new Date(p.start_time).getTime());
        }
        return sum;
      }, 0) / 60000 // minutes
    : 0;

  const totalDowntime = pauses?.reduce((sum, p) => {
    if (p.start_time && p.end_time) {
      return sum + (new Date(p.end_time).getTime() - new Date(p.start_time).getTime());
    }
    return sum;
  }, 0) / 60000 || 0;

  const operatingTime = Math.max(totalPlannedTime - totalDowntime, 0);
  const availability = totalPlannedTime > 0 ? (operatingTime / totalPlannedTime) * 100 : 0;

  // Performance (simplified - based on completion rate)
  const completedProcesses = processRecords?.filter((p) => p.status === "completed").length || 0;
  const totalProcesses = processRecords?.length || 0;
  const performance = totalProcesses > 0 ? (completedProcesses / totalProcesses) * 100 : 0;

  // Quality
  const passedInspections = qualityInspections?.filter((i) => i.status === "passed").length || 0;
  const totalInspected = qualityInspections?.filter(
    (i) => i.status === "passed" || i.status === "failed"
  ).length || 0;
  const quality = totalInspected > 0 ? (passedInspections / totalInspected) * 100 : 0;

  // OEE
  const oee = (availability * performance * quality) / 10000;

  const getOEEColor = (value: number) => {
    if (value >= 85) return "text-green-500";
    if (value >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getOEEBgColor = (value: number) => {
    if (value >= 85) return "bg-green-500";
    if (value >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  const oeeData = [
    { name: "OEE", value: oee, fill: oee >= 85 ? "#22c55e" : oee >= 60 ? "#eab308" : "#ef4444" },
    { name: "Remaining", value: 100 - oee, fill: "#e5e7eb" },
  ];

  const componentsData = [
    { name: "Disponibilidad", value: availability.toFixed(1), target: 90 },
    { name: "Rendimiento", value: performance.toFixed(1), target: 95 },
    { name: "Calidad", value: quality.toFixed(1), target: 99 },
  ];

  // Downtime by reason
  const downtimeByReason = pauses?.reduce((acc: Record<string, number>, p) => {
    const reason = p.reason || "Sin especificar";
    const duration = p.start_time && p.end_time
      ? (new Date(p.end_time).getTime() - new Date(p.start_time).getTime()) / 60000
      : 0;
    acc[reason] = (acc[reason] || 0) + duration;
    return acc;
  }, {}) || {};

  const downtimeData = Object.entries(downtimeByReason)
    .map(([reason, minutes]) => ({ reason, minutes: Math.round(minutes) }))
    .sort((a, b) => b.minutes - a.minutes)
    .slice(0, 5);

  const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6"];

  return (
    <div className="space-y-6">
      {/* OEE Overview */}
      <div className="grid gap-6 md:grid-cols-4">
        {/* Main OEE Gauge */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Gauge className="w-5 h-5" />
              OEE General
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={oeeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    startAngle={180}
                    endAngle={0}
                    dataKey="value"
                  >
                    {oeeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center -mt-16">
              <p className={cn("text-4xl font-bold", getOEEColor(oee))}>{oee.toFixed(1)}%</p>
              <p className="text-sm text-muted-foreground mt-1">
                {oee >= 85 ? "Excelente" : oee >= 60 ? "Aceptable" : "Necesita mejora"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* OEE Components */}
        <Card className="md:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">Componentes del OEE</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-3">
              {/* Availability */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-500" />
                    <span className="font-medium">Disponibilidad</span>
                  </div>
                  <span className={cn("font-bold", getOEEColor(availability))}>
                    {availability.toFixed(1)}%
                  </span>
                </div>
                <Progress
                  value={availability}
                  className={cn("h-3", `[&>div]:${getOEEBgColor(availability)}`)}
                />
                <p className="text-xs text-muted-foreground">
                  Tiempo operativo: {operatingTime.toFixed(0)} min de {totalPlannedTime.toFixed(0)} min
                </p>
              </div>

              {/* Performance */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-500" />
                    <span className="font-medium">Rendimiento</span>
                  </div>
                  <span className={cn("font-bold", getOEEColor(performance))}>
                    {performance.toFixed(1)}%
                  </span>
                </div>
                <Progress
                  value={performance}
                  className={cn("h-3", `[&>div]:${getOEEBgColor(performance)}`)}
                />
                <p className="text-xs text-muted-foreground">
                  {completedProcesses} de {totalProcesses} procesos completados
                </p>
              </div>

              {/* Quality */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-500" />
                    <span className="font-medium">Calidad</span>
                  </div>
                  <span className={cn("font-bold", getOEEColor(quality))}>
                    {quality.toFixed(1)}%
                  </span>
                </div>
                <Progress
                  value={quality}
                  className={cn("h-3", `[&>div]:${getOEEBgColor(quality)}`)}
                />
                <p className="text-xs text-muted-foreground">
                  {passedInspections} de {totalInspected} inspecciones aprobadas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Downtime Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Analisis de Paradas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {downtimeData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={downtimeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" unit=" min" />
                    <YAxis dataKey="reason" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number) => [`${value} min`, "Tiempo"]}
                      contentStyle={{
                        backgroundColor: "hsl(var(--background))",
                        border: "1px solid hsl(var(--border))",
                      }}
                    />
                    <Bar dataKey="minutes" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>No hay paradas registradas</p>
                </div>
              </div>
            )}
            <div className="mt-4 p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Tiempo total de parada</span>
                <span className="font-bold text-red-500">{totalDowntime.toFixed(0)} min</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* OEE by Machine */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">OEE por Maquina</CardTitle>
          </CardHeader>
          <CardContent>
            {machines && machines.length > 0 ? (
              <div className="space-y-4">
                {machines.slice(0, 5).map((machine) => {
                  // Simplified OEE per machine (would need actual data per machine)
                  const machineOEE = Math.random() * 30 + 65; // Placeholder
                  return (
                    <div key={machine.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{machine.name}</span>
                        <span className={cn("font-bold", getOEEColor(machineOEE))}>
                          {machineOEE.toFixed(1)}%
                        </span>
                      </div>
                      <Progress
                        value={machineOEE}
                        className="h-2"
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No hay maquinas registradas
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* OEE Legend */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500" />
              <span>Excelente (85%+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500" />
              <span>Aceptable (60-85%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500" />
              <span>Necesita mejora (&lt;60%)</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
